import { useEffect } from 'react';
import { driver, Config } from 'driver.js';
import 'driver.js/dist/driver.css';

interface OnboardingTourProps {
    autoStart?: boolean;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ autoStart = true }) => {
    useEffect(() => {
        if (!autoStart) return;

        // Check if user has already seen the tour
        const hasSeenTour = localStorage.getItem('hasSeenOnboardingTour');

        if (!hasSeenTour) {
            // Wait for DOM to be ready and navigation to complete
            const timer = setTimeout(() => {
                startTour();
            }, 1500);

            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStart]);

    const startTour = () => {
        // Expand first project menu so tour can highlight submenu items
        if ((window as any).expandFirstProjectMenu) {
            (window as any).expandFirstProjectMenu();
        }

        // Wait for menu expansion animation to complete before starting tour
        setTimeout(() => {
            startTourWithConfig();
        }, 500);
    };

    const startTourWithConfig = () => {
        const driverConfig: Config = {
            showProgress: true,
            progressText: '{{current}} of {{total}}',
            nextBtnText: 'Next',
            prevBtnText: 'Previous',
            doneBtnText: 'Get Started!',
            onDestroyed: () => {
                // Mark tour as seen
                localStorage.setItem('hasSeenOnboardingTour', 'true');
            },
            steps: [
                {
                    element: '.page-content',
                    popover: {
                        title: '👋 Welcome to Integr.dev!',
                        description: `
                            <div style="line-height: 1.6;">
                                <p>Let's take a quick 60-second tour to show you the essentials.</p>
                                <p><strong>You can skip this tour anytime</strong> or restart it from the Help menu.</p>
                            </div>
                        `,
                        side: 'top',
                        align: 'center',
                    },
                },
                {
                    element: '[id^="project-nav-"]',
                    popover: {
                        title: '📂 Your Projects',
                        description: `
                            <div style="line-height: 1.6;">
                                <p>Your workspace is organized into <strong>projects</strong>.</p>
                                <p>Each project can have multiple endpoints and its own API keys.</p>
                                <p className="mb-0"><em>This is your default project - you can create more later!</em></p>
                            </div>
                        `,
                        side: 'right',
                        align: 'start',
                    },
                },
                {
                    popover: {
                        title: '🎯 What Are Endpoints?',
                        description: `
                            <div style="line-height: 1.6;">
                                <p><strong>Endpoints</strong> are custom AI-powered APIs you create to process documents.</p>
                                <ul style="margin-top: 8px; padding-left: 20px;">
                                    <li>Extract data from invoices, resumes, contracts</li>
                                    <li>Process PDFs, images, or text</li>
                                    <li>Get structured JSON output</li>
                                </ul>
                                <p className="mb-0"><em>Let's see where to create them...</em></p>
                            </div>
                        `,
                        side: 'top',
                        align: 'center',
                    },
                },
                {
                    element: '[href*="/endpoints"]',
                    popover: {
                        title: '📍 Endpoints Page',
                        description: `
                            <div style="line-height: 1.6;">
                                <p>Click here to view and manage your endpoints.</p>
                                <p>You'll see:</p>
                                <ul style="margin-top: 8px; padding-left: 20px;">
                                    <li><strong>Sandbox</strong> - for testing</li>
                                    <li><strong>Production</strong> - for live use</li>
                                </ul>
                                <p className="mb-0">Create your first endpoint with the "+ Create" button.</p>
                            </div>
                        `,
                        side: 'right',
                        align: 'start',
                    },
                },
                {
                    element: '[href*="/api-keys"]',
                    popover: {
                        title: '🔑 API Keys',
                        description: `
                            <div style="line-height: 1.6;">
                                <p>Get your API keys here to authenticate your requests.</p>
                                <p>Each project has:</p>
                                <ul style="margin-top: 8px; padding-left: 20px;">
                                    <li><strong>Sandbox key</strong> - for development and testing</li>
                                    <li><strong>Production key</strong> - for live applications</li>
                                </ul>
                                <p className="mb-0"><em>Keep your production keys secure!</em></p>
                            </div>
                        `,
                        side: 'right',
                        align: 'start',
                    },
                },
                {
                    element: '[href*="/jobs"]',
                    popover: {
                        title: '📊 Jobs Monitor',
                        description: `
                            <div style="line-height: 1.6;">
                                <p>Monitor all your API requests and see processing results here.</p>
                                <p>Track:</p>
                                <ul style="margin-top: 8px; padding-left: 20px;">
                                    <li>Request status (success/failed)</li>
                                    <li>Processing time</li>
                                    <li>Cost and token usage</li>
                                    <li>Detailed logs for debugging</li>
                                </ul>
                            </div>
                        `,
                        side: 'right',
                        align: 'start',
                    },
                },
                {
                    popover: {
                        title: '🚀 You\'re All Set!',
                        description: `
                            <div style="line-height: 1.6;">
                                <p><strong>Ready to build?</strong> Here's what to do next:</p>
                                <ol style="margin-top: 8px; padding-left: 20px;">
                                    <li>Create your first endpoint</li>
                                    <li>Copy your API key</li>
                                    <li>Make your first API call</li>
                                </ol>
                                <p className="mb-0"><em>Need help? Click the (?) icons throughout the portal or check our documentation.</em></p>
                                <div style="margin-top: 12px; padding: 8px; background: #e7f3ff; border-left: 3px solid #2196F3; border-radius: 4px;">
                                    <small><strong>💡 Tip:</strong> Start with a simple invoice or receipt processing endpoint to see how it works!</small>
                                </div>
                            </div>
                        `,
                        side: 'top',
                        align: 'center',
                    },
                },
            ],
        };

        const driverObj = driver(driverConfig);
        driverObj.drive();
    };

    // Expose startTour function globally for manual restart
    (window as any).restartOnboardingTour = () => {
        startTour();
    };

    return null; // This component doesn't render anything
};

export default OnboardingTour;
